<svelte:options
  customElement={{
    tag: "oc-tooltip-v1",
    shadow: "none",
    /*                                            */
    extend: window.__components.extend({
      delegateFocus: true,
    }),
    props: {
      position: { type: "String" },
    },
  }}
/>

<script lang="ts">
  import { fade } from "svelte/transition";
  import type { Props } from "./TooltipV1.types";

  export let position: Props["position"] = "bottom";

  let tooltipOpen = false;

  let tooltipElement: HTMLSpanElement;
  const initialPosition = position;
  let positionChanged = false;

  let tooltipContainerClass = "";
  let trianglePosition = position === "bottom" ? "top" : "bottom";

  function adjustTooltipPosition() {
    const { left, right, top, bottom, height } = tooltipElement.getBoundingClientRect();
    const spaceBelow = window.innerHeight - bottom;

    if (initialPosition === "top") {
      /*                                                          */
      if (top < 0) {
        position = "bottom";
        positionChanged = true;
      } else if (top > height * 2 + 16 && positionChanged) {
        position = initialPosition;
      }
    }

    if (initialPosition === "bottom") {
      /*                                                             */
      if (bottom > window.innerHeight) {
        position = "top";
        positionChanged = true;
      } else if (spaceBelow > height * 2 + 16 && positionChanged) {
        position = initialPosition;
      }
    }

    /*                                                            */
    if (right > window.innerWidth - 16) {
      /*                                      */
      tooltipContainerClass = "right";
    }

    /*                                                           */
    if (left < 0) {
      /*                                       */
      tooltipContainerClass = "left";
    }
  }

  $: if (tooltipElement) {
    if (tooltipOpen) {
      adjustTooltipPosition();
    } else {
      tooltipContainerClass = "";
    }
  }

  $: trianglePosition = position === "bottom" ? "top" : "bottom";
</script>

<!-- svelte-ignore a11y-no-static-element-interactions -->
<!-- eslint-disable-next-line svelte/valid-compile -->
<!-- svelte-ignore a11y-mouse-events-have-key-events -->
<div
  class="tooltip-box"
  on:mouseover={() => {
    tooltipOpen = true;
  }}
  on:mouseleave={() => {
    tooltipOpen = false;
  }}
  on:focusin={() => {
    tooltipOpen = true;
  }}
  on:focusout={() => {
    tooltipOpen = false;
  }}
  on:keydown={({ code }) => {
    if (tooltipOpen && code === "Escape") {
      tooltipOpen = false;
    }
  }}
>
  <slot />

  {#if tooltipOpen}
    <div in:fade={{ delay: 500, duration: 200 }} out:fade={{ delay: 200, duration: 200 }}>
      <span class="triangle triangle--{trianglePosition}"></span>
      <div
        class="tooltip tooltip--{tooltipContainerClass} tooltip--{position}"
        bind:this={tooltipElement}
      >
        <slot name="tooltip-content" />
      </div>
    </div>
  {/if}
</div>

<style lang="scss" global>
  @use "@otto-ec/design-tokens/component" as tokens;
  @use "../../../common/scss/mixins/mixins.scss";

  :host {
    display: inline-block;
  }
  .tooltip-box {
    position: relative;

    .triangle {
      position: absolute;
      border-style: solid;
      border-width: 0 4px 4px;
      border-color: transparent;
      border-bottom-color: tokens.$oc-component-tooltip-background-color;

      &--top {
        bottom: 0;
        margin-bottom: -4px;
        left: 50%;
        transform: translateX(-50%);
      }

      &--bottom {
        top: 0;
        margin-top: -8px;
        transform: rotate(180deg) translateX(-50%);
        left: calc(50% - 8px);
      }
    }

    .tooltip {
      left: 50%;
      transform: translateX(calc(-50% - 6px));
      position: absolute;
      max-width: 300px;
      width: max-content;
      background-color: tokens.$oc-component-tooltip-background-color;
      color: tokens.$oc-component-tooltip-copy-color;
      border-radius: tokens.$oc-component-tooltip-border-radius;
      padding: tokens.$oc-component-tooltip-spacing-y tokens.$oc-component-tooltip-spacing-x;
      margin: 0 tokens.$oc-component-tooltip-frame-spacing;
      font: tokens.$oc-component-tooltip-copy-font;

      &--top {
        bottom: calc(100% + 8px);
      }

      &--right {
        left: auto;
        right: 0;
        transform: translateX(calc(tokens.$oc-component-tooltip-frame-spacing * 2));
      }

      &--bottom {
        top: calc(100% + 4px);
      }

      &--left {
        left: 0;
        transform: translateX(calc(tokens.$oc-component-tooltip-frame-spacing * -1 * 2));
      }
    }
  }
</style>
